import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiImage,
  FiChevronDown,
  FiCheck,
  FiLoader,
} from 'react-icons/fi';
import { uploadFilesToCloudinary } from '../../utils/cloudinary';
import { toast } from '../../utils/toast';
import { Tooltip } from './Tooltip';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

const ZOOM_OPTIONS = [
  { label: '75%', value: '75' },
  { label: '100%', value: '100' },
  { label: '125%', value: '125' },
  { label: '150%', value: '150' },
];

const BLOCK_TYPES = [
  { label: 'Paragraph text', value: 'p' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Quote', value: 'blockquote' },
];

const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Courier New', value: 'Courier New' },
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Tell the service provider about your project...',
  className = '',
  minHeight = '220px',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // States
  const [zoom, setZoom] = useState('100');
  const [blockType, setBlockType] = useState('p');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isList, setIsList] = useState(false);
  const [alignment, setAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [isUploading, setIsUploading] = useState(false);

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState<'zoom' | 'block' | 'font' | null>(null);

  // Helper to convert plain text with linebreaks to html if needed
  const formatInitialValue = (val: string) => {
    if (!val) return '';
    // If it already contains HTML tags, return as is
    if (/<[a-z][\s\S]*>/i.test(val)) return val;
    // Otherwise wrap paragraphs
    return val
      .split('\n')
      .map((line) => (line.trim() ? `<p>${line}</p>` : '<p><br></p>'))
      .join('');
  };

  // Sync external value to contentEditable div
  useEffect(() => {
    if (editorRef.current) {
      const formatted = formatInitialValue(value);
      if (editorRef.current.innerHTML !== formatted) {
        if (document.activeElement !== editorRef.current || !value) {
          editorRef.current.innerHTML = formatted;
        }
      }
    }
  }, [value]);

  // Update active state of toolbar controls based on selection
  const updateToolbarStates = useCallback(() => {
    if (!editorRef.current) return;
    try {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
      setIsList(document.queryCommandState('insertUnorderedList'));

      if (document.queryCommandState('justifyCenter')) setAlignment('center');
      else if (document.queryCommandState('justifyRight')) setAlignment('right');
      else setAlignment('left');

      // Detect block type
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = (node as HTMLElement).tagName.toLowerCase();
            if (['h1', 'h2', 'h3', 'p', 'blockquote'].includes(tag)) {
              setBlockType(tag);
              break;
            }
          }
          node = node.parentNode;
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Execute formatting command
  const executeCommand = (command: string, value: string = '') => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    updateToolbarStates();
    onChange(editorRef.current.innerHTML);
  };

  const handleBlockTypeChange = (type: string) => {
    setBlockType(type);
    executeCommand('formatBlock', `<${type}>`);
    setOpenDropdown(null);
  };

  const handleFontFamilyChange = (font: string) => {
    setFontFamily(font);
    executeCommand('fontName', font);
    setOpenDropdown(null);
  };

  const handleZoomChange = (zoomVal: string) => {
    setZoom(zoomVal);
    setOpenDropdown(null);
  };

  const uploadAndInsertImage = async (file: File) => {
    if (isUploading) return;
    setIsUploading(true);
    try {
      const results = await uploadFilesToCloudinary([file], {
        folder: 'editor_images',
        uploadPreset: 'service_preset',
      });
      if (results.length > 0 && results[0].secure_url) {
        executeCommand('insertImage', results[0].secure_url);
      } else {
        toast.error('Failed to get secure URL from image upload.');
      }
    } catch (error) {
      console.error('Error uploading image to Cloudinary:', error);
      toast.error('Image upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInsertImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadAndInsertImage(files[0]);
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        uploadAndInsertImage(imageFiles[0]);
      } else {
        toast.error('Please drop image files only.');
      }
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.rich-editor-dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const zoomFactor = parseInt(zoom, 10) / 100;

  return (
    <div
      className={`border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden bg-zinc-900/20 flex flex-col ${className}`}
    >
      {/* Toolbar */}
      <div className="relative z-20 border-b border-white/10 bg-zinc-900/40">
        <div className="flex flex-wrap items-center gap-1 p-2 sm:p-3">
          {/* Zoom Dropdown */}
          <div className="relative rich-editor-dropdown">
            <Tooltip content="Zoom Level">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'zoom' ? null : 'zoom')}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-white/10 text-zinc-300 text-[11px] sm:text-xs transition-colors"
                aria-label={`Zoom Level: ${zoom}%`}
              >
                <span>{zoom}%</span>
                <FiChevronDown size={12} className="text-zinc-400" />
              </button>
            </Tooltip>

            {openDropdown === 'zoom' && (
              <div className="absolute top-full left-0 mt-1 w-24 bg-zinc-900 border border-white/10 rounded-xl shadow-xl py-1 z-30">
                {ZOOM_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleZoomChange(opt.value)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <span>{opt.label}</span>
                    {zoom === opt.value && <FiCheck size={12} className="text-blue-400" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* Block Type Dropdown */}
          <div className="relative rich-editor-dropdown">
            <Tooltip content="Paragraph Format">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'block' ? null : 'block')}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-white/10 text-zinc-300 text-[11px] sm:text-xs transition-colors"
                aria-label={`Paragraph Format: ${BLOCK_TYPES.find((b) => b.value === blockType)?.label || 'Paragraph text'}`}
              >
                <span>
                  {BLOCK_TYPES.find((b) => b.value === blockType)?.label || 'Paragraph text'}
                </span>
                <FiChevronDown size={12} className="text-zinc-400" />
              </button>
            </Tooltip>

            {openDropdown === 'block' && (
              <div className="absolute top-full left-0 mt-1 w-36 bg-zinc-900 border border-white/10 rounded-xl shadow-xl py-1 z-30">
                {BLOCK_TYPES.map((bt) => (
                  <button
                    key={bt.value}
                    type="button"
                    onClick={() => handleBlockTypeChange(bt.value)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <span>{bt.label}</span>
                    {blockType === bt.value && <FiCheck size={12} className="text-blue-400" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* Font Family Dropdown */}
          <div className="relative rich-editor-dropdown">
            <Tooltip content="Font Family">
              <button
                type="button"
                onClick={() => setOpenDropdown(openDropdown === 'font' ? null : 'font')}
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg hover:bg-white/10 text-zinc-300 text-[11px] sm:text-xs transition-colors"
                aria-label={`Font Family: ${fontFamily}`}
              >
                <span>{fontFamily}</span>
                <FiChevronDown size={12} className="text-zinc-400" />
              </button>
            </Tooltip>

            {openDropdown === 'font' && (
              <div className="absolute top-full left-0 mt-1 w-36 bg-zinc-900 border border-white/10 rounded-xl shadow-xl py-1 z-30">
                {FONT_FAMILIES.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => handleFontFamilyChange(f.value)}
                    style={{ fontFamily: f.value }}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <span>{f.label}</span>
                    {fontFamily === f.value && <FiCheck size={12} className="text-blue-400" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* Formatting Toggles */}
          <div className="flex items-center gap-0.5">
            <Tooltip content="Bold (Ctrl+B)">
              <button
                type="button"
                onClick={() => executeCommand('bold')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  isBold
                    ? 'bg-blue-500/20 text-blue-400 font-bold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Bold (Ctrl+B)"
              >
                <FiBold size={14} />
              </button>
            </Tooltip>
            <Tooltip content="Italic (Ctrl+I)">
              <button
                type="button"
                onClick={() => executeCommand('italic')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  isItalic
                    ? 'bg-blue-500/20 text-blue-400 font-bold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Italic (Ctrl+I)"
              >
                <FiItalic size={14} />
              </button>
            </Tooltip>
            <Tooltip content="Underline (Ctrl+U)">
              <button
                type="button"
                onClick={() => executeCommand('underline')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  isUnderline
                    ? 'bg-blue-500/20 text-blue-400 font-bold'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Underline (Ctrl+U)"
              >
                <FiUnderline size={14} />
              </button>
            </Tooltip>
          </div>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* List */}
          <Tooltip content="Bullet List">
            <button
              type="button"
              onClick={() => executeCommand('insertUnorderedList')}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                isList
                  ? 'bg-blue-500/20 text-blue-400 font-bold'
                  : 'text-zinc-400 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Bullet List"
            >
              <FiList size={14} />
            </button>
          </Tooltip>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* Alignment */}
          <div className="flex items-center gap-0.5">
            <Tooltip content="Align Left">
              <button
                type="button"
                onClick={() => executeCommand('justifyLeft')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  alignment === 'left'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Align Left"
              >
                <FiAlignLeft size={14} />
              </button>
            </Tooltip>
            <Tooltip content="Align Center">
              <button
                type="button"
                onClick={() => executeCommand('justifyCenter')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  alignment === 'center'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Align Center"
              >
                <FiAlignCenter size={14} />
              </button>
            </Tooltip>
            <Tooltip content="Align Right">
              <button
                type="button"
                onClick={() => executeCommand('justifyRight')}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  alignment === 'right'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'text-zinc-400 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Align Right"
              >
                <FiAlignRight size={14} />
              </button>
            </Tooltip>
          </div>

          <div className="w-px h-4 bg-white/10 mx-0.5 sm:mx-1" />

          {/* Insert Image */}
          <Tooltip content="Insert Image">
            <span className="inline-flex">
              <button
                type="button"
                onClick={handleInsertImage}
                disabled={isUploading}
                className="p-1.5 sm:p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                aria-label="Insert Image"
              >
                {isUploading ? (
                  <FiLoader size={14} className="animate-spin text-blue-400" />
                ) : (
                  <FiImage size={14} />
                )}
              </button>
            </span>
          </Tooltip>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Editable Area */}
      <div className="relative p-4 sm:p-6 overflow-y-auto" style={{ minHeight }}>
        {isUploading && (
          <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center gap-2 text-sm text-zinc-300 backdrop-blur-sm">
            <FiLoader className="animate-spin text-blue-500" size={20} />
            <span>Uploading image...</span>
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyUp={updateToolbarStates}
          onMouseUp={updateToolbarStates}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            fontSize: `${14 * zoomFactor}px`,
            lineHeight: 1.6,
          }}
          className="rich-editor outline-none text-zinc-300 text-[13px] sm:text-[15px] space-y-2 [&_p]:mb-2 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-bold [&_blockquote]:border-l-2 [&_blockquote]:border-blue-500 [&_blockquote]:pl-3 [&_blockquote]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:max-w-full [&_img]:rounded-lg"
          data-placeholder={placeholder}
        />
      </div>

      <style>{`
        .rich-editor:empty:before {
          content: attr(data-placeholder);
          color: #52525b;
          pointer-events: none;
          display: block;
        }
      `}</style>
    </div>
  );
};
